import type { Notification } from '@/types/notification';

interface NotificationSection {
  title: string;
  data: Notification[];
}

type SectionKey = 'TODAY' | 'YESTERDAY' | 'THIS WEEK' | 'LAST WEEK' | 'THIS MONTH' | 'OLDER';

const SECTION_ORDER: readonly SectionKey[] = [
  'TODAY',
  'YESTERDAY',
  'THIS WEEK',
  'LAST WEEK',
  'THIS MONTH',
  'OLDER',
];

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getMonday(date: Date): Date {
  const d = startOfDay(date);
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return d;
}

function getSectionKey(createdAt: string, now: Date): SectionKey {
  const date = new Date(createdAt);
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const notifDay = startOfDay(date);

  if (notifDay.getTime() === today.getTime()) return 'TODAY';
  if (notifDay.getTime() === yesterday.getTime()) return 'YESTERDAY';

  const thisMonday = getMonday(now);
  if (notifDay >= thisMonday) return 'THIS WEEK';

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(lastMonday.getDate() - 7);
  if (notifDay >= lastMonday) return 'LAST WEEK';

  if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
    return 'THIS MONTH';
  }

  return 'OLDER';
}

export function groupNotificationsByTime(notifications: Notification[]): NotificationSection[] {
  const now = new Date();
  const buckets = new Map<SectionKey, Notification[]>();

  for (const notification of notifications) {
    const key = getSectionKey(notification.createdAt, now);
    const existing = buckets.get(key);
    if (existing) {
      existing.push(notification);
    } else {
      buckets.set(key, [notification]);
    }
  }

  return SECTION_ORDER
    .filter((key) => buckets.has(key))
    .map((key) => ({ title: key, data: buckets.get(key)! }));
}
